"use client";

import Link from "@/components/LocalizedLink";
import { useTranslation } from "@/lib/i18n";
import { formatInsightDate, readingTimeLabel } from "@/lib/tools-i18n/insights";
import { TagPill } from "./TagPill";
import type { InsightArticle } from "@/lib/insights/types";

interface InsightCardProps {
  article: InsightArticle;
  /** Show full excerpt vs single-line teaser. */
  variant?: "default" | "compact";
}

export function InsightCard({ article, variant = "default" }: InsightCardProps) {
  const { language } = useTranslation();
  const { frontmatter, readingTime } = article;
  const href = `/insights/${frontmatter.slug}/`;
  return (
    <article className="group flex flex-col p-6 rounded-xl border border-border bg-surface hover:border-primary/40 hover:bg-surface-light transition-all duration-200">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {frontmatter.tags.slice(0, 3).map((t) => (
          <TagPill key={t} tag={t} />
        ))}
      </div>
      <h3 lang="en" className="text-lg font-semibold text-foreground mb-2 leading-snug">
        <Link
          href={href}
          className="hover:text-primary-light transition-colors focus-visible:outline-none focus-visible:underline"
        >
          {frontmatter.title}
        </Link>
      </h3>
      {variant === "default" ? (
        <p lang="en" className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-3">
          {frontmatter.excerpt}
        </p>
      ) : null}
      <div className="mt-auto flex items-center gap-2 text-xs text-muted-foreground pt-2">
        <time dateTime={frontmatter.date}>{formatInsightDate(frontmatter.date, language)}</time>
        <span aria-hidden="true">·</span>
        <span>{readingTimeLabel(readingTime, language)}</span>
      </div>
    </article>
  );
}
